import { styled } from "@mui/material/styles";
import { InternalStandardProps, Radio, Typography } from "@mui/material";
import { paymentMethodToString } from "JS/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppCircularProgress } from "../Progress/AppCircularProgress";
import { AppTypography } from "../AppTypography";

const Root = styled("div")(() => ({
    display: "flex",
}));

const Grey600Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const StyledIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "22px",
    color: theme.palette.grey[600],
}));

const RadioContainer = styled("span")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
    color: theme.palette.grey[500],
    "&.Mui-checked": {
        color: theme.palette.grey[500],
    },
}));

const CreditsWrapper = styled(AppTypography)(({ theme }) => ({
    background:
        theme.palette.mode === "light"
            ? theme.palette.grey[200]
            : theme.palette.grey[900],
    color: theme.palette.grey[600],
    borderRadius: "50%",
    marginLeft: theme.spacing(1),
    padding: theme.spacing(1),
}));

export enum PaymentMethod {
    MY_CREDITS = "MY_CREDITS",
    CARD = "CARD",
}

export interface PaymentMethodRadioProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {
    credits: number;
    onMethodChange: (method: PaymentMethod) => void;
    method: PaymentMethod;
    availableCreditsLoading: boolean;
    contentPrice?: number;
}

export const PaymentMethodRadio = (props: PaymentMethodRadioProps) => {
    const {
        className,
        credits,
        method,
        onMethodChange,
        availableCreditsLoading,
        contentPrice,
        ...rest
    } = props;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onMethodChange(event.target.value as PaymentMethod);
    };

    const controlProps = (item: string) => ({
        checked: method === item,
        onChange: handleChange,
        value: item,
        name: "payment-method-radio",
        inputProps: { "aria-label": item },
    });

    return (
        <Root {...rest} className={className}>
            <RadioContainer>
                <StyledRadio {...controlProps(PaymentMethod.MY_CREDITS)} />
                <Grey600Typography variant="body1">
                    {paymentMethodToString(PaymentMethod.MY_CREDITS)}
                </Grey600Typography>
                {availableCreditsLoading && (
                    <AppCircularProgress
                        style={{
                            height: "20px",
                            width: "20px",
                        }}
                    />
                )}
                {!availableCreditsLoading && (
                    <CreditsWrapper variant="body1">
                        {credits || 0}
                    </CreditsWrapper>
                )}
            </RadioContainer>
            {contentPrice > 0 && (
                <span
                    style={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <StyledRadio {...controlProps(PaymentMethod.CARD)} />
                    <StyledIcon icon={["fas", "credit-card"]} />
                </span>
            )}
        </Root>
    );
};
